import React, { useCallback, useEffect, useState } from "react";
import {
  DataGrid,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
} from "@mui/x-data-grid";
import { Alert, SelectChangeEvent } from "@mui/material";
import {
  ConfirmAction,
  MultipleSelectField,
  Row,
  ToggleActionProps,
} from "../types";
import Snackbar from "@mui/material/Snackbar";
import { AlertProps } from "@mui/material/Alert";
import { dataGridStyle, getRowClassName } from "../styles";
import { computeMutation } from "../utils/computeMutation";
import EditToolbar from "./EditToolbar";
import { ConfirmDialog } from "./ConfirmDialog";
import { getColumns } from "../utils/getColumns";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import {
  fetchAllCountriesDataAsync,
  setMainCountriesForRegions,
} from "../../../features/countries/countriesAllSlice";
import { fetchUsersDataAsync } from "../../../features/users/usersSlice";
import { api } from "../../../api/api";
import { fetchRegionsDataAsync } from "../../../features/regions/regionsSlice";
import { User } from "../../../features/users/types";
import { Region } from "../../../features/regions/types";

const AdminPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    users: { data: usersData },
    regions: { data: regionsData },
    countriesAll: { data: countriesAll, mainCountriesForRegions },
  } = useSelector((state: RootState) => state);
  const [rows, setRows] = useState<Row[]>([]);
  const [rowToSaveId, setRowToSaveId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchUsersDataAsync());
    dispatch(fetchRegionsDataAsync());
    dispatch(fetchAllCountriesDataAsync());
  }, [dispatch]);

  useEffect(() => {
    if (usersData) {
      const updatedInitialRows = usersData.map((user) => ({
        id: user.id || "",
        password: "",
        email: user.email,
        region_id: user.region_id,
        name: user.name,
        surname: user.surname,
        countries: user.countries,
        isActive: !user.disabled,
        isAdmin: user.admin,
      }));
      setRows(updatedInitialRows);
    }
  }, [usersData]);

  useEffect(() => {
    const mainCountriesForRegions = countriesAll.filter(
      (country) => country.region_name === country.country_name.toUpperCase()
    );
    mainCountriesForRegions &&
      dispatch(setMainCountriesForRegions(mainCountriesForRegions));
  }, [countriesAll, dispatch]);

  const [action, setAction] = React.useState<ConfirmAction | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] =
    React.useState(false);

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const [snackbar, setSnackbar] = React.useState<Pick<
    AlertProps,
    "children" | "severity"
  > | null>(null);

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSave = useCallback(
    (id: GridRowId) => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View },
      });
    },
    [rowModesModel]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await api.deleteUser(id);
        setSnackbar({
          children: `User successfully deleted.`,
          severity: "success",
        });
        setRows(rows.filter((row) => row.id !== id));
      } catch (e) {
        setSnackbar({
          children: `Failed to delete user.`,
          severity: "error",
        });
      }
    },
    [rows, setSnackbar, setRows]
  );

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleSelectChange = useCallback(
    (
      event: SelectChangeEvent<string[]>,
      userId: string,
      type: MultipleSelectField
    ) => {
      const {
        target: { value },
      } = event;

      if (type) {
        const updatedRows = rows.map((row) => {
          if (row.id === userId) {
            const selectedValues = typeof value === "string" ? [value] : value;

            const flattenedSelections = Array.from(
              new Set(selectedValues.flat())
            ) as Region[] | string[];

            return {
              ...row,
              countries:
                type === "region_id" ? ([] as string[]) : row.countries,
              [type]:
                (type === "region_id" && value) ||
                (type === "countries" && flattenedSelections),
            };
          }
          return row;
        });

        setRows(updatedRows);
      }
    },
    [rows]
  );

  const processRowUpdate = React.useCallback(
    async (newRow: GridRowModel<Row>, oldRow: GridRowModel<Row>) => {
      try {
        const fittedRow = rows.find((r) => r.id === newRow.id);
        const mainCountryId = mainCountriesForRegions?.find(
          (country) => country.region_id === fittedRow?.region_id
        )?.id;

        if (!fittedRow || !mainCountryId) {
          throw new Error("Region or row not found.");
        }
        const countries = Array.from(
          new Set([mainCountryId, ...fittedRow.countries])
        );

        const payload: User = {
          email: newRow.email,
          name: newRow.name,
          surname: newRow.surname,
          region_id: fittedRow.region_id,
          countries,
          admin: newRow.isAdmin,
          disabled: !newRow.isActive,
        };

        if (newRow.isNew || newRow.password.length) {
          payload.pass = newRow.password;
        }
        if (!newRow.isNew) {
          payload.id = newRow.id;
        }

        const success = await api.addOrUpdateUser(payload);

        if (success) {
          dispatch(fetchUsersDataAsync());
          const updatedRow = {
            ...newRow,
            password: "",
            region_id: fittedRow.region_id,
            countries: fittedRow.countries,
            isNew: false,
          };
          setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
          handleSave(newRow.id);
          setSnackbar({
            children: "User successfully saved.",
            severity: "success",
          });
          return updatedRow;
        } else {
          setSnackbar({
            children: "Failed to save user.",
            severity: "error",
          });
        }

        return oldRow;
      } catch (error: any) {
        setSnackbar({ children: error.message, severity: "error" });
      }
    },
    [dispatch, handleSave, mainCountriesForRegions, rows]
  );

  const handleProcessRowUpdateError = useCallback((error: Error) => {
    setSnackbar({ children: error.message, severity: "error" });
  }, []);

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const toggleAction: ToggleActionProps = useCallback(
    (targetRowId, toggleProperty) => () => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === targetRowId
            ? {
                ...row,
                [toggleProperty]: !row[toggleProperty],
              }
            : row
        )
      );
    },
    []
  );

  const handleCloseSnackbar = () => setSnackbar(null);

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleModalOpen =
    (row: GridRowModel<Row>, action: ConfirmAction) => () => {
      setRowToSaveId(row.id);
      setAction(action);
      setShowConfirmationModal(true);
      computeMutation(action);
    };

  const handleNo = () => {
    setRowToSaveId(null);
  };

  const handleYes = async (action: ConfirmAction) => {
    try {
      if (rowToSaveId) {
        if (action === "save") {
          handleSave(rowToSaveId);
        }
        if (action === "delete") {
          await handleDelete(rowToSaveId);
        }
        setRowToSaveId(null);
      }
    } catch (error) {
      setSnackbar({ children: "Something went wrong.", severity: "error" });
      setRowToSaveId(null);
    }
  };

  const columns = getColumns({
    rows,
    rowModesModel,
    toggleAction,
    regionsData,
    countriesAll,
    handleModalOpen,
    handleCancelClick,
    handleEditClick,
    handleSelectChange,
  });

  return (
    <>
      {rowToSaveId && action && (
        <ConfirmDialog
          action={action}
          isOpen={showConfirmationModal}
          handleNo={handleNo}
          handleYes={handleYes}
        />
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        slots={{
          toolbar: EditToolbar,
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel },
        }}
        getRowHeight={() => (countriesAll.length > 1 ? "auto" : 80)}
        getRowClassName={getRowClassName}
        sx={dataGridStyle}
      />
      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </>
  );
};

export default AdminPanel;
